import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  AcquisitionChannel,
  Currency,
  LoanStatusExternal,
  ProductExternal,
  PurchaseCountry,
} from '@nemuru-eng/js-api-client';
import { useEffect, useRef } from 'react';

import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

import { TimeInterval } from '../shared/entities/TimeInterval';
import { LoanStatusExternalScoreCard } from './components/LoanStatusExternalScoreCard';
import { useMetricAggregateViews } from './hooks/useMetricAggregateViews';

interface MetricAggregateViewsProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal[];
  productExternal?: ProductExternal[];
  purchaseCountries?: PurchaseCountry[];
  acquisitionChannel?: AcquisitionChannel[];
}

export const MetricAggregateViews = ({
  currency,
  timeInterval,
  loanStatusExternal,
  productExternal,
  purchaseCountries,
}: MetricAggregateViewsProps) => {
  const { sm } = useScreen();
  const { customBorderRadius } = useCustomTheme();

  const {
    state: { metricsRefreshToken },
  } = useOperationsContext();

  const { getMetricAggregateViews, filteredMetrics, hasRequested } = useMetricAggregateViews({
    currency,
    timeInterval,
    productExternal,
    purchaseCountries,
    loanStatusExternal,
  });

  const lastToken = useRef(metricsRefreshToken);

  useEffect(() => {
    getMetricAggregateViews();
  }, [timeInterval]);

  useEffect(() => {
    if (lastToken.current === metricsRefreshToken) return;
    lastToken.current = metricsRefreshToken;
    void getMetricAggregateViews(true);
  }, [metricsRefreshToken]);

  return (
    <Box display={'flex'} flexDirection={sm ? 'row' : 'column-reverse'} gridGap={16}>
      <Box flexGrow={1}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: sm ? 'repeat(auto-fit, minmax(180px, 1fr))' : '1fr',
            rowGap: sm ? 32 : 16,
            columnGap: 16,
          }}
        >
          {hasRequested === false &&
            loanStatusExternal.map((i) => (
              <Skeleton
                key={`skeleton-mav-${i}`}
                variant="rect"
                width={'100%'}
                height={sm ? 132 : 60}
                style={{ borderRadius: customBorderRadius.paper }}
              />
            ))}
          {hasRequested === true &&
            loanStatusExternal.map((status, i) => (
              <Box key={`scorecard-mav-${i}`}>
                <LoanStatusExternalScoreCard
                  currency={currency}
                  timeInterval={timeInterval}
                  loanStatusExternal={status}
                  filteredMetrics={filteredMetrics}
                />
              </Box>
            ))}
        </div>
      </Box>
    </Box>
  );
};
